import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import { Card } from "@tokenterminal/core/ui/layout/card/Card"
import { Badge } from "@tokenterminal/core/ui/messaging/badge/Badge"
import { EmptyState } from "@tokenterminal/core/ui/messaging/emptyState/EmptyState"
import { formatNumber } from "@tokenterminal/core/utils/sharedFormatter"
import { useThemeContext } from "@tokenterminal/ui/Theme/ThemeContext"
import Link from "next/link"
import { useState, type ReactNode } from "react"
import { Treemap, ResponsiveContainer, Tooltip } from "recharts"
import { type TooltipProps } from "recharts/types/component/Tooltip"
import { AllItemsModal } from "./AllItemsModal"
import {
  getBadgeColor,
  getColor,
  getTwBadgeTextColor,
  type ColorMode,
} from "./colorUtils"
import {
  isOtherGroupItem,
  type OtherGroupTreemapItem,
  type TreemapItem,
} from "./useTreemapData"

const PADDING = 14

const CustomTooltip = ({
  active,
  payload,
  metricFormat,
}: TooltipProps<number, string> & {
  metricFormat: string
}) => {
  if (active && payload && payload.length > 0) {
    const data = payload[0].payload as TreemapItem

    let content = null
    if (isOtherGroupItem(data)) {
      content = (
        <>
          <p className="text-xs font-medium leading-none">
            {data.otherGroupCount} other
            {data.otherGroupCount === 1 ? "" : "s"}
          </p>
          <p className="text-xs text-fg-secondary mt-1">
            {(data.percentageOfTotal * 100).toFixed(2)}% of total
          </p>
        </>
      )
    } else {
      content = (
        <>
          <p className="text-xs font-medium leading-none">{data.name}</p>
          <div className="flex items-center gap-1 mt-0.5">
            <p className="text-xs uppercase text-fg-default">
              {formatNumber({
                value: data.originalValue,
                format: "compact",
                metricFormat,
              })}
            </p>{" "}
            {data.change !== null && (
              <Badge
                color={getBadgeColor(data.change)}
                variant="outline"
                size="xsmall"
                className="!text-xs"
              >
                {data.change > 0 ? "+" : ""}
                {(data.change * 100).toFixed(2)}%
              </Badge>
            )}
          </div>
          <p className="text-xs text-fg-secondary mt-1">
            {(data.percentageOfTotal * 100).toFixed(2)}% of total
          </p>
        </>
      )
    }

    return (
      <div className="bg-bg-surface p-3 rounded-lg shadow-md border border-border-default flex flex-col items-start gap-0.5">
        {content}
      </div>
    )
  }
  return null
}

const OtherGroupContent = ({
  x,
  y,
  width,
  height,
  handleOpenModal,
  colorMode,
  theme,
  itemData,
  index,
  fillColor,
}: {
  x: number
  y: number
  width: number
  height: number
  handleOpenModal: () => void
  colorMode: ColorMode
  theme?: "light" | "dark"
  itemData: OtherGroupTreemapItem
  index: number
  fillColor: string
}) => {
  return (
    <g>
      <defs>
        <pattern
          id="pinstripe"
          patternUnits="userSpaceOnUse"
          width="3"
          height="3"
          patternTransform="rotate(45)"
        >
          <line
            x1="0"
            y1="0"
            x2="0"
            y2="3"
            stroke="hsl(var(--fg-default) / 10%)"
            strokeWidth="2"
          />
        </pattern>
        <linearGradient id="otherGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="rgba(255,255,255,0.9)" />
          <stop offset="100%" stopColor="rgba(255,255,255,0)" />
        </linearGradient>
        <pattern
          id="gradientOverlay"
          patternUnits="userSpaceOnUse"
          width="100%"
          height="100%"
        >
          <rect width="100%" height="100%" fill="url(#otherGradient)" />
        </pattern>
      </defs>
      <foreignObject x={x} y={y} width={width} height={height}>
        <div
          onClick={handleOpenModal}
          tabIndex={0}
          aria-role="button"
          className="w-full h-full flex items-center justify-center opacity-20 hover:opacity-30 cursor-pointer"
          style={{
            boxShadow: `inset 0 0 2px rgba(255,255,255,1)`,
          }}
        >
          <ResponsiveContainer
            width="100%"
            height="100%"
            className="pointer-events-none"
          >
            <Treemap
              data={itemData.otherChildren}
              dataKey="treemapValue"
              nameKey="name"
              aspectRatio={1}
              isAnimationActive={false}
              stroke="hsl(var(--bg-default))"
              fill="#8884d8"
              // @ts-ignore
              content={({
                x,
                y,
                width,
                height,
                depth,
                index: nestedIndex,
              }: {
                x: number
                y: number
                width: number
                height: number
                depth: number
                index: number
              }) => {
                const otherChildrenData = itemData.otherChildren[nestedIndex]

                return (
                  <g>
                    <rect
                      x={x}
                      y={y}
                      width={width}
                      height={height}
                      style={{
                        fill: getColor({
                          colorMode,
                          // @ts-ignore
                          change: otherChildrenData?.change || 0,
                          index: index + nestedIndex,
                          theme: theme || "light",
                        }),
                        stroke: "hsl(var(--bg-default))",
                        strokeWidth: 1,
                        opacity: 0.7,
                      }}
                    />
                  </g>
                )
              }}
            />
          </ResponsiveContainer>
        </div>
      </foreignObject>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        style={{
          pointerEvents: "none",
          fill: `url(#pinstripe)`,
          stroke: "hsl(var(--bg-default))",
          strokeWidth: 1,
          boxShadow: `inset 0 0 2px ${fillColor}`,
        }}
      />
    </g>
  )
}

type TreeMapContentProps = {
  treeMapData: TreemapItem[]
  root: any
  x: number
  y: number
  width: number
  height: number
  index: number
  name: string
  colorMode: ColorMode
  metricFormat?: string
  depth: number
  handleOpenModal: () => void
}

// TODO: Clean up item rendering

const TreeMapContent = ({
  root,
  x,
  y,
  depth,
  width,
  height,
  index,
  name,
  colorMode,
  metricFormat,
  handleOpenModal,
}: TreeMapContentProps): ReactNode => {
  const { theme } = useThemeContext()

  if (depth === 0) {
    return null
  }

  const itemData = root.children[index]
  const isOther = isOtherGroupItem(itemData)

  // Common dimensions and calculations
  const nameFontSize = 13
  const valueFontSize = 12
  const renderableHeight = height - PADDING - PADDING
  const renderableWidth = width - PADDING - PADDING
  const iconSize = 14
  const iconBottomMargin = 4
  const gap = 4
  const heightOfContent =
    iconSize + nameFontSize + valueFontSize + iconBottomMargin + gap

  // Common values
  const value = itemData?.originalValue
  const valueText = `${formatNumber({ value, format: "compact", metricFormat })}`

  // Fill color calculation (for both regular and other items)
  const fillColor = isOther
    ? "hsl(var(--neutral-500))"
    : getColor({
        colorMode,
        change: itemData.change,
        index,
        theme: theme || "light",
      })

  // Determine content based on available space (common logic)
  let content = null
  if (renderableWidth < iconSize || renderableHeight < iconSize) {
    content = null
  } else if (isOther) {
    content = (
      <div className="text-xs opacity-75">+{itemData.otherGroupCount}</div>
    )
  } else if (renderableWidth < 60 || renderableHeight < heightOfContent) {
    content = (
      <Icon
        as={itemData.icon}
        width={iconSize}
        height={iconSize}
        className="opacity-75"
      />
    )
  } else {
    // Full content for regular items
    const regularItem = itemData
    content = (
      <div className="flex flex-col" style={{ gap: `${gap}px` }}>
        <Icon
          as={regularItem.icon}
          style={{ marginBottom: `${iconBottomMargin}px` }}
          className="opacity-75"
        />
        <div
          style={{
            fontSize: `${nameFontSize}px`,
            lineHeight: `${nameFontSize}px`,
            color: "hsl(var(--fg-default))",
            fontWeight: 500,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {name}
        </div>
        {renderableHeight > heightOfContent && (
          <div
            className="flex items-center gap-1 text-fg-default whitespace-nowrap truncate ellipsis"
            style={{
              fontSize: `${valueFontSize}px`,
              lineHeight: `${valueFontSize}px`,
            }}
          >
            <span className="opacity-100">{valueText}</span>
            {colorMode === "trend" && regularItem.change !== null && (
              <div
                className={`font-[var(--font-geist-mono)] ${getTwBadgeTextColor(regularItem.change)}`}
              >
                ({regularItem.change > 0 ? "+" : ""}
                {regularItem.change.toLocaleString(undefined, {
                  style: "percent",
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
                )
              </div>
            )}
            {colorMode === "categorical" &&
              regularItem.percentageOfTotal !== null && (
                <div className="opacity-60">
                  (
                  {regularItem.percentageOfTotal.toLocaleString(undefined, {
                    style: "percent",
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                  )
                </div>
              )}
          </div>
        )}
      </div>
    )
  }

  return (
    <g>
      {isOther ? (
        <OtherGroupContent
          x={x}
          y={y}
          width={width}
          height={height}
          handleOpenModal={handleOpenModal}
          colorMode={colorMode}
          theme={theme}
          itemData={itemData}
          index={index}
          fillColor={fillColor}
        />
      ) : (
        <Link href={`/markets/${itemData.slug}`}>
          <rect
            className="opacity-40 hover:opacity-50"
            x={x}
            y={y}
            width={width}
            height={height}
            style={{
              fill: fillColor,
              stroke: "hsl(var(--bg-default))",
              strokeWidth: 1,
              boxShadow: `inset 0 0 2px ${fillColor}`,
            }}
          />
        </Link>
      )}
      <foreignObject
        x={x}
        y={y}
        width={width}
        height={height}
        className="pointer-events-none"
      >
        <div style={{ padding: `${PADDING}px`, height: "100%", width: "100%" }}>
          {content}
        </div>
      </foreignObject>
    </g>
  )
}

export const MarketSectorsTreemap = ({
  colorMode,
  height,
  treeMapData,
  metricFormat,
}: {
  colorMode: ColorMode
  height: number
  treeMapData: TreemapItem[]
  metricFormat: string
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const handleOpenModal = () => {
    setIsModalOpen(true)
  }

  return (
    <>
      <ResponsiveContainer
        width="99.99%"
        height={height}
        className="rounded-md overflow-hidden relative"
      >
        {treeMapData.length === 0 ? (
          <Card className="absolute inset-0 flex items-center justify-center bg-neutral-500/10">
            <EmptyState
              headingText="No data available"
              bodyText="No data available for this metric"
              size="small"
            />
          </Card>
        ) : (
          <Treemap
            data={treeMapData}
            dataKey="treemapValue"
            stroke="transparent"
            isAnimationActive={false}
            aspectRatio={2 / 1}
            content={
              // @ts-ignore
              <TreeMapContent
                colorMode={colorMode}
                metricFormat={metricFormat}
                handleOpenModal={handleOpenModal}
              />
            }
          >
            <Tooltip
              content={(props) => (
                // @ts-ignore
                <CustomTooltip metricFormat={metricFormat} {...props} />
              )}
              isAnimationActive={false}
            />
          </Treemap>
        )}
      </ResponsiveContainer>
      <AllItemsModal
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        treeMapData={treeMapData}
        colorMode={colorMode}
        metricFormat={metricFormat}
      />
    </>
  )
}
